/**
 * Layout Component
 *
 * Arranges the main panels of the application:
 * - Left side: Output Window (image) + Script Window (stacked vertically)
 * - Middle: Node View (React Flow)
 * - Right side: Properties Panel (vertical panel, full height)
 * - Bottom: Timeline (spans full width)
 *
 * The layout uses CSS Grid for the main structure.
 *
 * ┌─────────────────┬──────────────────────────┬──────────────┐
 * │                 │                          │              │
 * │  Output Window  │      Node View           │              │
 * │  (Image)        │      (React Flow)        │ Properties   │
 * │                 │                          │ Panel        │
 * ├─────────────────┤                          │              │
 * │                 │                          │              │
 * │  Script Window  │                          │              │
 * │  (Screenplay)   │                          │              │
 * │                 │                          │              │
 * ├─────────────────┴──────────────────────────┴──────────────┤
 * │  Timeline                                                 │
 * └───────────────────────────────────────────────────────────┘
 */

import { CSSProperties, useCallback } from 'react';

// Import panel components
import NodeView from './NodeView/NodeView';
import ReferenceLibraryView from './ReferenceLibraryView/ReferenceLibraryView';
import Timeline from './Timeline/Timeline';
import OutputWindow from './OutputWindow/OutputWindow';
import ScriptWindow from './ScriptWindow/ScriptWindow';
import PropertiesPanel from './PropertiesPanel/PropertiesPanel';
import MenuBar from './MenuBar/MenuBar';
import ResizeHandle from './ResizeHandle/ResizeHandle';
import BeatShelf from './BeatShelf/BeatShelf';
import SpeedIndicator from './SpeedIndicator/SpeedIndicator';
import NotificationDisplay from './NotificationDisplay/NotificationDisplay';
import ViewTabs from './ViewTabs/ViewTabs';

// Import stores for project info display
import { useProjectStore, useIsDirty } from '@/stores/projectStore';
import { useLayoutStore } from '@/stores/layoutStore';
import { useViewStore } from '@/stores/viewStore';

// Import app constants
import { APP_NAME, APP_VERSION, APP_COPYRIGHT } from '@/constants/app';

/**
 * Layout Component
 *
 * CSS Grid layout with named areas for clarity:
 * - "output nodeview" - top row
 * - "script nodeview" - second row (script shares space with nodeview)
 * - "script properties" - properties below nodeview
 * - "timeline timeline" - bottom row spans both columns
 */
function Layout() {
  // Get project name and dirty state for the title bar
  const projectName = useProjectStore((state) => state.projectName);
  const isDirty = useIsDirty();
  const activeView = useViewStore((state) => state.activeView);

  // Get layout dimensions from store
  const leftPanelWidth = useLayoutStore((state) => state.leftPanelWidth);
  const rightPanelWidth = useLayoutStore((state) => state.rightPanelWidth);
  const outputHeight = useLayoutStore((state) => state.outputHeight);
  const timelineHeight = useLayoutStore((state) => state.timelineHeight);
  const shelfHeight = useLayoutStore((state) => state.shelfHeight);

  // Get resize actions from store
  const setLeftPanelWidth = useLayoutStore((state) => state.setLeftPanelWidth);
  const setRightPanelWidth = useLayoutStore((state) => state.setRightPanelWidth);
  const setOutputHeight = useLayoutStore((state) => state.setOutputHeight);
  const setTimelineHeight = useLayoutStore((state) => state.setTimelineHeight);
  const setShelfHeight = useLayoutStore((state) => state.setShelfHeight);

  // ---------------------------------------------------------------------------
  // RESIZE HANDLERS
  // ---------------------------------------------------------------------------

  const handleLeftDividerResize = useCallback(
    (delta: number) => {
      setLeftPanelWidth(leftPanelWidth + delta);
    },
    [leftPanelWidth, setLeftPanelWidth]
  );

  const handleRightDividerResize = useCallback(
    (delta: number) => {
      setRightPanelWidth(rightPanelWidth - delta);
    },
    [rightPanelWidth, setRightPanelWidth]
  );

  const handleOutputDividerResize = useCallback(
    (delta: number) => {
      // Calculate height percentage based on delta
      // The left panel spans the full height (minus menu bar and footer)
      const containerHeight = window.innerHeight - 32 - 24;
      const deltaPercentage = (delta / containerHeight) * 100;

      setOutputHeight(outputHeight + deltaPercentage);
    },
    [outputHeight, setOutputHeight]
  );

  const handleTimelineDividerResize = useCallback(
    (delta: number) => {
      setTimelineHeight(timelineHeight - delta);
    },
    [timelineHeight, setTimelineHeight]
  );

  const handleShelfDividerResize = useCallback(
    (delta: number) => {
      setShelfHeight(shelfHeight - delta);
    },
    [shelfHeight, setShelfHeight]
  );

  // ---------------------------------------------------------------------------
  // STYLES
  // ---------------------------------------------------------------------------

  const layoutStyles: CSSProperties = {
    display: 'grid',
    gridTemplateColumns: `${leftPanelWidth}px 1fr ${rightPanelWidth}px`,
    gridTemplateRows: `1fr ${shelfHeight}px ${timelineHeight}px 24px`,
    gridTemplateAreas: `
      "left-panel middle-main right-props"
      "left-panel shelf shelf"
      "left-panel timeline timeline"
      "footer footer footer"
    `,
    height: 'calc(100vh - 32px)',
    overflow: 'hidden',
    backgroundColor: 'var(--nb-bg-primary)',
    position: 'relative',
  };

  const leftPanelStyles: CSSProperties = {
    gridArea: 'left-panel',
    display: 'flex',
    flexDirection: 'column',
    borderRight: '1px solid var(--nb-border-primary)',
    overflow: 'hidden',
  };

  const leftTopStyles: CSSProperties = {
    flex: `${outputHeight}`,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  };

  const leftBottomStyles: CSSProperties = {
    flex: `${100 - outputHeight}`,
    display: 'flex',
    flexDirection: 'column',
    borderTop: '1px solid var(--nb-border-primary)',
    minHeight: '200px',
    overflow: 'hidden',
  };

  const middleMainStyles: CSSProperties = {
    gridArea: 'middle-main',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  };

  const rightPropsStyles: CSSProperties = {
    gridArea: 'right-props',
    borderLeft: '1px solid var(--nb-border-primary)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  };

  const shelfStyles: CSSProperties = {
    gridArea: 'shelf',
    overflow: 'hidden',
  };

  const timelineStyles: CSSProperties = {
    gridArea: 'timeline',
    borderTop: '1px solid var(--nb-border-primary)',
    overflow: 'hidden',
  };

  const footerStyles: CSSProperties = {
    gridArea: 'footer',
    backgroundColor: 'var(--nb-bg-tertiary)',
    borderTop: '1px solid var(--nb-border-primary)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    padding: '0 var(--nb-space-sm)',
    fontSize: 'var(--nb-font-size-xs)',
    color: 'var(--nb-text-muted)',
  };

  const headerStyles: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 'var(--nb-space-xs) var(--nb-space-sm)',
    backgroundColor: 'var(--nb-bg-tertiary)',
    borderBottom: '1px solid var(--nb-border-primary)',
    fontSize: 'var(--nb-font-size-xs)',
    color: 'var(--nb-text-secondary)',
    userSelect: 'none',
  };

  // ---------------------------------------------------------------------------
  // RENDER
  // ---------------------------------------------------------------------------

  return (
    <>
      <MenuBar />
      <div style={layoutStyles}>
        {/* Left Panel: Output + Script (stacked vertically) */}
        <div style={leftPanelStyles}>
          {/* Left Top: Output Window (shows current beat's image) */}
          <div style={leftTopStyles} data-panel="left-top">
            <div style={headerStyles}>
              <span>Output</span>
            </div>
            <OutputWindow />
          </div>

          {/* Left Bottom: Script Window (shows screenplay text) */}
          <div style={leftBottomStyles} data-panel="left-bottom">
            <div style={headerStyles}>
              <span>Script</span>
            </div>
            <ScriptWindow />
          </div>
        </div>

        {/* Middle: Node View or Reference Library (React Flow canvas) */}
        <div style={middleMainStyles}>
          <div style={headerStyles}>
            <span>
              {projectName}
              {isDirty && ' •'}
            </span>
            <ViewTabs />
          </div>
          <div style={{ flex: 1, overflow: 'hidden' }}>
            {activeView === 'beats' ? (
              <NodeView />
            ) : (
              <ReferenceLibraryView />
            )}
          </div>
        </div>

        {/* Right: Properties Panel (edit selected node) */}
        <div style={rightPropsStyles}>
          <div style={headerStyles}>
            <span>Properties</span>
          </div>
          <PropertiesPanel />
        </div>

        {/* Beat Shelf (aligned with middle column) */}
        <div style={shelfStyles}>
          <BeatShelf />
        </div>

        {/* Bottom: Timeline (aligned with middle column) */}
        <div style={timelineStyles}>
          <Timeline />
        </div>

        {/* Footer */}
        <div style={footerStyles}>
          <span>{APP_COPYRIGHT}</span>
        </div>

        {/* Resize Handles */}
        {/* Left vertical divider (between left panel and center) */}
        <ResizeHandle
          direction="vertical"
          onResize={handleLeftDividerResize}
          style={{
            left: `${leftPanelWidth}px`,
            transform: 'translateX(-2.5px)',
            top: 0,
            height: `calc(100% - 24px)`,
          }}
        />

        {/* Right vertical divider (between center and right panel) */}
        <ResizeHandle
          direction="vertical"
          onResize={handleRightDividerResize}
          style={{
            right: `${rightPanelWidth}px`,
            transform: 'translateX(-2.5px)',
            top: 0,
            height: `calc(100% - ${shelfHeight}px - ${timelineHeight}px - 24px)`,
          }}
        />

        {/* Horizontal divider between Output and Script (inside left panel) */}
        <ResizeHandle
          direction="horizontal"
          onResize={handleOutputDividerResize}
          style={{
            top: `calc((100vh - 56px) * ${outputHeight / 100})`,
            left: 0,
            width: `${leftPanelWidth}px`,
            transform: 'translateY(-2.5px)',
            zIndex: 10,
          }}
        />

        {/* Shelf height divider (spans middle and right columns) */}
        <ResizeHandle
          direction="horizontal"
          onResize={handleShelfDividerResize}
          style={{
            bottom: `calc(${timelineHeight}px + ${shelfHeight}px + 24px)`,
            left: `${leftPanelWidth}px`,
            right: 0,
            transform: 'translateY(-2.5px)',
          }}
        />

        {/* Timeline height divider (spans middle and right columns) */}
        <ResizeHandle
          direction="horizontal"
          onResize={handleTimelineDividerResize}
          style={{
            bottom: `calc(${timelineHeight}px + 24px)`,
            left: `${leftPanelWidth}px`,
            right: 0,
            transform: 'translateY(-2.5px)',
          }}
        />

        {/* Speed Indicator (shows when playback speed > 1x) */}
        <SpeedIndicator />
        
        {/* Notification Display (shows warnings and info messages) */}
        <NotificationDisplay />
      </div>
    </>
  );
}

export default Layout;
